import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { doc, getDoc, updateDoc, arrayUnion, deleteDoc, Timestamp, collection, getDocs, query, orderBy, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { FileStorageService } from '../../services/FileStorageService';
import { db as firestore, storage } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { UserManagementService } from '../../services/UserManagementService';
import {
  StoreInfoCard,
  NotesSection,
  NotesList
} from '../../components/store';
import { TabbedInterface } from '../../components/common';
import { OptimizedHeritageOrderForm } from '../../components/forms/OptimizedHeritageOrderForm';
import { OptimizedLivingstonOrderForm } from '../../components/forms/OptimizedLivingstonOrderForm';
import { OptimizedFlowerOrderForm } from '../../components/forms/OptimizedFlowerOrderForm';
import { OptimizedVegetableOrderForm } from '../../components/forms/OptimizedVegetableOrderForm';
import { StoreOrderSuppliesForm } from '../../components/forms/StoreOrderSuppliesForm';
import { PlanogramOrderForm } from '../../components/forms/PlanogramOrderForm';
import { GlobalOrderProvider, useGlobalOrderData } from '../../hooks/useGlobalOrderData';
import { GlobalSubmitButton } from '../../components/forms/shared/GlobalSubmitButton';
import { GlobalOrderSummary } from '../../components/forms/shared/GlobalOrderSummary';
import { PreviewOrderButton } from '../../components/forms/shared/PreviewOrderButton';
import { CustomerInformationSection, OrderInformationSection } from '../../components/forms/shared/SharedFormSections';
import { useCustomerData } from '../../components/forms/shared/CustomerDataService';
import { OrderPreviewScreen } from '../../components/forms/shared/OrderPreviewScreen';
import { OrderSuppliesPreviewScreen } from '../../components/forms/shared/OrderSuppliesPreviewScreen';
import { HistoricalOrderPreviewScreen } from '../../components/forms/shared/HistoricalOrderPreviewScreen';
import { StoreHistory } from '../../components/common/StoreHistory';
import { ResetOrderButton } from '../../components/forms/shared/ResetOrderButton';
import { Store, Note, Media, Document } from '../../types/storeTypes';
import { 
  PageContainer, 
  MainContent,
  Card,
  FlexColumn,
  Title,
  Text,
  Button,
  Spacer
} from '../../styles/StyledComponents';
import styled from 'styled-components';
import { ThemeColors, Spacing, Typography, BorderRadius, Shadows } from '../../styles/ThemeStyles';
import { Store as StoreIcon, FileText, Package, Flower, Carrot, ShoppingCart, History, Package2, Home, ArrowLeft, LayoutGrid } from 'lucide-react';

// Custom styled components for mobile optimization
const MobileMainContent = styled(MainContent)`
  padding: ${Spacing.md};
  padding-bottom: ${Spacing.xxl};
`;

const MobileTitle = styled(Title)`
  font-size: ${Typography.fontSize.xl};
  margin-bottom: ${Spacing.md};
`;

const MobileText = styled(Text)`
  font-size: ${Typography.fontSize.md};
`;

const MobileCard = styled(Card)`
  padding: ${Spacing.lg};
  margin-bottom: ${Spacing.md};
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  background: none;
  border: none;
  color: ${ThemeColors.primary.main};
  font-size: ${Typography.fontSize.md};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  padding: ${Spacing.xs} 0;
  margin-bottom: ${Spacing.sm};
  transition: color 0.2s ease;
  
  &:hover {
    color: ${ThemeColors.primary.dark};
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const BreadcrumbContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  margin-bottom: ${Spacing.md};
  font-size: ${Typography.fontSize.md};
  color: ${ThemeColors.text.secondary};
  flex-wrap: wrap;
  padding-top: ${Spacing.sm};
  
  @media (max-width: 480px) {
    font-size: ${Typography.fontSize.sm};
    gap: ${Spacing.xs};
  }
`;

const BreadcrumbItem = styled.span<{ clickable?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${props => props.clickable ? ThemeColors.primary.main : ThemeColors.text.secondary};
  cursor: ${props => props.clickable ? 'pointer' : 'default'};
  transition: color 0.2s ease;
  font-weight: ${props => props.clickable ? Typography.fontWeight.medium : Typography.fontWeight.regular};
  
  &:hover {
    color: ${props => props.clickable ? ThemeColors.primary.dark : ThemeColors.text.secondary};
  }
  
  svg {
    width: 18px;
    height: 18px;
    
    @media (max-width: 480px) {
      width: 16px;
      height: 16px;
    }
  }
`;

const BreadcrumbSeparator = styled.span`
  color: ${ThemeColors.border.medium};
  margin: 0 4px;
  font-size: ${Typography.fontSize.md};
  
  @media (max-width: 480px) {
    margin: 0 2px;
    font-size: ${Typography.fontSize.sm};
  }
`;

const BreadcrumbText = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
  
  @media (max-width: 480px) {
    max-width: 150px;
  }
`;

const STORE_DETAIL_TAB_SLUGS: readonly string[] = ['store-visit', 'store-order', 'store-history', 'order-supplies'];

export const StoreDetailsScreenMobile: React.FC = () => {
  const { storeId } = useParams<{ storeId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const [store, setStore] = useState<Store | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [isMenuCollapsed, setIsMenuCollapsed] = useState(true);
  const [userFullName, setUserFullName] = useState<string>('');
  
  // Preview state
  // Helper function to get localStorage key for this store
  const getStorageKey = (key: string) => `store_${storeId}_${key}`;
  
  // Load persisted state from localStorage on mount
  const loadPersistedState = () => {
    if (!storeId) return { showPreview: false, activeTab: 'store-visit', selectedHistoricalOrderId: null };
    
    // Check for tab query parameter first (overrides persisted state)
    const tabParam = searchParams.get('tab');
    if (tabParam && STORE_DETAIL_TAB_SLUGS.includes(tabParam)) {
      return {
        showPreview: false,
        activeTab: tabParam,
        selectedHistoricalOrderId: null
      };
    }
    
    try {
      const persistedShowPreview = localStorage.getItem(getStorageKey('showPreview'));
      const persistedActiveTab = localStorage.getItem(getStorageKey('activeTab'));
      const persistedHistoricalOrderId = localStorage.getItem(getStorageKey('selectedHistoricalOrderId'));
      
      return {
        showPreview: persistedShowPreview === 'true',
        activeTab: persistedActiveTab || 'store-visit',
        selectedHistoricalOrderId: persistedHistoricalOrderId || null
      };
    } catch (error) {
      console.error('Error loading persisted state:', error);
      return { showPreview: false, activeTab: 'store-visit', selectedHistoricalOrderId: null };
    }
  };
  
  // Save state to localStorage
  const saveState = (key: string, value: any) => {
    if (!storeId) return;
    try {
      localStorage.setItem(getStorageKey(key), String(value));
    } catch (error) {
      console.error(`Error saving state ${key}:`, error);
    }
  };
  
  // Load initial state from localStorage
  const persistedState = loadPersistedState();
  
  const [showPreview, setShowPreview] = useState(persistedState.showPreview);
  
  // Historical order preview state
  const [selectedHistoricalOrderId, setSelectedHistoricalOrderId] = useState<string | null>(persistedState.selectedHistoricalOrderId);
  
  // Active tab state for TabbedInterface
  const [activeTab, setActiveTab] = useState<string>(persistedState.activeTab);

  const tabChangeFromUiRef = useRef(false);

  const previewUrlValue = showPreview
    ? activeTab === 'order-supplies'
      ? 'supplies'
      : 'order'
    : null;

  useEffect(() => {
    if (!storeId) return;
    const tabSlug = STORE_DETAIL_TAB_SLUGS.includes(activeTab) ? activeTab : 'store-visit';
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', tabSlug);
        if (previewUrlValue) {
          next.set('preview', previewUrlValue);
        } else {
          next.delete('preview');
        }
        return next;
      },
      { replace: true }
    );
  }, [activeTab, previewUrlValue, storeId, setSearchParams]);

  const tabFromUrl = searchParams.get('tab');
  useEffect(() => {
    if (!storeId) return;
    if (!tabFromUrl || !STORE_DETAIL_TAB_SLUGS.includes(tabFromUrl)) {
      return;
    }
    if (tabChangeFromUiRef.current) {
      tabChangeFromUiRef.current = false;
      return;
    }
    if (tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
      setShowPreview(false);
      saveState('showPreview', 'false');
      saveState('activeTab', tabFromUrl);
    }
  }, [tabFromUrl, activeTab, storeId]);

  // Effect to save state changes to localStorage
  useEffect(() => {
    if (storeId) {
      saveState('activeTab', activeTab);
    }
  }, [activeTab, storeId]);
  
  useEffect(() => {
    if (storeId) {
      saveState('showPreview', showPreview ? 'true' : 'false');
    }
  }, [showPreview, storeId]);
  
  useEffect(() => {
    if (storeId) {
      saveState('selectedHistoricalOrderId', selectedHistoricalOrderId || '');
    }
  }, [selectedHistoricalOrderId, storeId]);

  // Customer and Order Information Component
  const CustomerAndOrderSections = () => {
    const { customerData, loading: customerLoading, error: customerError } = useCustomerData(storeId || '');
    const { orderData, updateOrderData } = useGlobalOrderData();
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    // Sync customer data with global order data when customer data is loaded
    React.useEffect(() => {
      if (customerData) {
        // ONLY sync customer info fields, don't overwrite order info fields (poNumber, specialInstructions, etc.)
        const customerInfo = {
          customerNumber: customerData.customerNumber,
          storeName: customerData.storeName,
          address: customerData.address,
          city: customerData.city,
          province: customerData.province,
          postalCode: customerData.postalCode,
          email: customerData.email,
          phoneNumber: customerData.phoneNumber
        };
        updateOrderData(customerInfo);
      }
    }, [customerData, updateOrderData]);

    const handleInputChange = (field: string, value: string | boolean) => {
      updateOrderData({ [field]: value });
      // Clear error when user starts typing
      if (errors[field]) {
        setErrors(prev => ({ ...prev, [field]: '' }));
      }
    };

    return (
      <div>
        {/* Order Summary and Preview Button - Moved to top */}
        <div style={{ marginBottom: Spacing.lg }}>
          <GlobalOrderSummary />
          <PreviewOrderButton onPreview={handleShowPreview} orderType="store-order" />
        </div>

        {/* Customer Information */}
        <CustomerInformationSection
          data={orderData}
          onChange={handleInputChange}
          errors={errors}
          loading={customerLoading}
          error={customerError}
        />

        {/* Order Information */}
        <OrderInformationSection
          data={orderData}
          onChange={handleInputChange}
          errors={errors}
        />
      </div>
    );
  };

  // Fetch store data from Firestore
  useEffect(() => {
    const fetchStore = async () => {
      if (!storeId) return;
      
      try {
        setIsLoading(true);
        const storeDocRef = doc(firestore, 'Stores', storeId);
        const storeDocSnap = await getDoc(storeDocRef);
        
        if (storeDocSnap.exists()) {
          const storeData = storeDocSnap.data();
          
          // Fetch notes from the subcollection (matching desktop version)
          const notesCollectionRef = collection(firestore, 'Stores', storeId, 'notes');
          const notesQuery = query(notesCollectionRef, orderBy('createdAt', 'desc'));
          const notesSnapshot = await getDocs(notesQuery);
          
          const notesData: Note[] = [];
          notesSnapshot.forEach((noteDoc) => {
            const noteData = noteDoc.data();
            
            // Ensure competitorData is properly structured
            let competitorData = undefined;
            if (noteData.competitorData && Array.isArray(noteData.competitorData)) {
              competitorData = noteData.competitorData.map((comp: any) => ({
                id: comp.id || '',
                name: comp.name || '',
                photoUrls: Array.isArray(comp.photoUrls) ? comp.photoUrls : (comp.photoUrls ? [comp.photoUrls] : []),
                stockLevel: comp.stockLevel || 0,
                displayPanels: comp.displayPanels !== undefined && comp.displayPanels !== null ? Number(comp.displayPanels) : undefined
              }));
            }
            
            notesData.push({
              id: noteDoc.id,
              text: noteData.text || '',
              date: noteData.date || '',
              time: noteData.time || '',
              author: noteData.author || '',
              salesRepName: noteData.salesRepName || '',
              visitType: noteData.visitType || undefined,
              competitors: noteData.competitors || [],
              competitorData: competitorData,
              media: noteData.media || [],
              documents: noteData.documents || [],
              nextVisitDate: noteData.nextVisitDate || '',
              storeLevel: noteData.storeLevel !== undefined && noteData.storeLevel !== null ? Number(noteData.storeLevel) : undefined,
              lowStockCategories: noteData.lowStockCategories || [],
              requestManagerContact: noteData.requestManagerContact || false,
              replies: noteData.replies || [],
              createdAt: noteData.createdAt
            });
          });
          
          // Map store data fields to match desktop version structure
          setStore({
            id: storeDocSnap.id,
            CustomerNo: storeData.CustomerNo || storeData.customerNo || '',
            Address: storeData.Address || storeData.address || '',
            StoreName: storeData.StoreName || storeData.name || storeData.StoreName || '',
            City: storeData.City || storeData.city || '',
            StoreNumber: storeData.StoreNumber || storeData.storeNumber || '',
            Prov: storeData.Prov || storeData.province || storeData.state || '',
            PostalCode: storeData.PostalCode || storeData.postalCode || storeData.pc || '',
            SortField: storeData.SortField || storeData.sortField || '',
            Telephone: storeData.Telephone || storeData.phone || '',
            SalesGroup: storeData.SalesGroup || storeData.salesGroup || '',
            EmailAdddress: storeData.EmailAdddress || storeData.EmailAddress || storeData.email || '',
            SalespersonNo: storeData.SalespersonNo || storeData.salespersonNo,
            UDF_Outr_PickupNo: storeData.UDF_Outr_PickupNo || storeData.pickupNo || storeData.PickupNumber,
            notes: notesData,
            // Include any other fields from storeData
            ...storeData
          } as Store);
        } else {
          setError('Store not found');
        }
      } catch (err) {
        console.error(t('storeDetails.fetchError', 'Error fetching store:'), err);
        setError('Failed to load store details');
      } finally {
        setIsLoading(false);
      }
    };

    fetchStore();
  }, [storeId]);

  // Fetch user profile to get firstName and lastName
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user?.id) return;
      
      try {
        const userProfile = await UserManagementService.getUserById(user.id);
        if (userProfile) {
          const fullName = userProfile.firstName && userProfile.lastName
            ? `${userProfile.firstName} ${userProfile.lastName}`.trim()
            : userProfile.displayName || user?.email || '';
          setUserFullName(fullName);
        } else {
          // Fallback to email if profile not found
          setUserFullName(user?.email || '');
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
        // Fallback to email if error
        setUserFullName(user?.email || '');
      }
    };

    fetchUserProfile();
  }, [user?.id, user?.email]);

  const handleSubmitNote = async (
    text: string, 
    salesRepName: string, 
    competitors: string[], 
    attachments: { media: File[], documents: File[] }, 
    nextVisitDate: string,
    compressionEnabled: boolean = true,
    storeLevel?: number,
    lowStockCategories?: string[],
    requestManagerContact?: boolean,
    competitorData?: { id: string; name: string; photos: File[]; stockLevel: number; displayPanels?: number }[],
    visitType?: 'setup' | 'maintenance' | 'requested' | 'other' | '',
    visitReason?: string,
    onUploadProgress?: (progress: any) => void
  ) => {
    if (!storeId || !store) return;
    
    try {
      const fileStorageService = FileStorageService.getInstance();
      const noteId = `note-${Date.now()}`;
      const uploadedBy = user?.email || 'Unknown';

      const { media, documents } = attachments;

      const uploadResults = await fileStorageService.uploadAllNoteFiles(
        storeId,
        noteId,
        media,
        documents,
        competitorData || [],
        uploadedBy,
        compressionEnabled,
        undefined,
        onUploadProgress
      );

      // Log any upload errors
      if (uploadResults.errors && uploadResults.errors.length > 0) {
        console.warn('⚠️ Some files failed to upload:', uploadResults.errors);
      }

      // Convert upload results to Media and Document objects
      const mediaResults: Media[] = uploadResults.media.map(result => ({
        name: result.name,
        type: result.type,
        url: result.url
      }));

      const documentResults: Document[] = uploadResults.documents.map(result => ({
        name: result.name,
        type: result.type,
        url: result.url
      }));

      // Build competitor data with uploaded photo URLs
      let competitorDataWithUrls: { id: string; name: string; photoUrls: string[]; stockLevel: number; displayPanels?: number }[] = [];
      if (competitorData && competitorData.length > 0) {
        for (const competitor of competitorData) {
          const photoUrls = (uploadResults.competitorPhotos[competitor.id] || []).map(r => r.url);
          competitorDataWithUrls.push({
            id: competitor.id,
            name: competitor.name,
            photoUrls: photoUrls,
            stockLevel: competitor.stockLevel,
            ...(competitor.displayPanels !== undefined && competitor.displayPanels > 0 ? { displayPanels: competitor.displayPanels } : {})
          });
        }
      }

      // Create the new note object
      const now = new Date();
      const formattedDate = now.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit' 
      });
      const formattedTime = now.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      });
      
      const newNote: Note = {
        id: noteId,
        text,
        salesRepName: salesRepName || user?.email || 'Unknown',
        author: user?.email || 'Anonymous',
        date: formattedDate,
        time: formattedTime,
        visitType: visitType || undefined,
        competitors: competitors || [],
        // Only include competitorData if it has values (Firestore doesn't allow undefined)
        ...(competitorDataWithUrls.length > 0 ? { competitorData: competitorDataWithUrls } : {}),
        media: mediaResults,
        documents: documentResults,
        nextVisitDate: nextVisitDate || undefined,
        storeLevel: storeLevel !== undefined && storeLevel !== null ? storeLevel : 100, // Default to 100 if not provided
        lowStockCategories: lowStockCategories || [],
        requestManagerContact: requestManagerContact || false,
        visitReason: visitReason || undefined,
        createdAt: Timestamp.now()
      };

      // Save note as a document in the notes subcollection (matching desktop version)
      const notesCollectionRef = collection(firestore, 'Stores', storeId, 'notes');
      const noteDocRef = doc(notesCollectionRef, noteId);
      
      // Ensure storeLevel is always saved as a number (default to 100 if not provided)
      const noteToSave: any = {
        ...newNote,
        storeLevel: typeof newNote.storeLevel === 'number' ? newNote.storeLevel : 100
      };
      
      // Remove undefined fields (Firestore doesn't support undefined)
      Object.keys(noteToSave).forEach(key => {
        if (noteToSave[key] === undefined) {
          delete noteToSave[key];
        }
      });
      
      console.log('Saving note with storeLevel:', noteToSave.storeLevel);
      await setDoc(noteDocRef, noteToSave);
      
      // Update the nextVisitDate on the store document if provided
      // SECURITY CHECK: Only update if the user is the assigned salesperson
      const storeDocRef = doc(firestore, 'Stores', storeId);
      
      // Convert nextVisitDate string to proper format for store record
      let nextVisitTimestamp = null;
      if (nextVisitDate) {
        let daysToAdd = 0;
        if (nextVisitDate === 'Next Week') {
          daysToAdd = 7;
        } else if (nextVisitDate === '+2 Weeks') {
          daysToAdd = 14;
        } else if (nextVisitDate === '+3 Weeks') {
          daysToAdd = 21;
        } else if (nextVisitDate === '+4 Weeks') {
          daysToAdd = 28;
        } else if (nextVisitDate === '+5 Weeks') {
          daysToAdd = 35;
        }
        
        if (daysToAdd > 0) {
          const nextVisitDateObj = new Date(Date.now() + daysToAdd * 24 * 60 * 60 * 1000);
          nextVisitTimestamp = Timestamp.fromDate(nextVisitDateObj);
        }
      }

      if (nextVisitTimestamp) {
        await updateDoc(storeDocRef, {
          nextVisitDate: nextVisitTimestamp
        });
      }

      // Update denormalized fields (notesCount, lastVisit, lastVisitedBy) for database-level sorting
      const { StoreService } = await import('../../services/StoreService');
      StoreService.updateStoreDenormalizedFields(storeId).catch(err => {
        console.warn('Failed to update denormalized fields:', err);
      });

      // Trigger notification for note added - notify all managers (fire-and-forget, don't block)
      if (user?.id || user?.email) {
        // Use setTimeout to make it truly non-blocking
        setTimeout(async () => {
          try {
            const { NotificationTriggerService } = await import('../../services/NotificationTriggerService');
            await NotificationTriggerService.triggerNoteAddedNotification(
              storeId,
              noteId,
              user?.id || user?.email || 'Unknown',
              store?.StoreName || ''
            );
          } catch (err) {
            console.error('Failed to trigger note notification (non-blocking):', err);
            // Silently fail - notification errors should not affect note submission
          }
        }, 0);
      }

      // Small delay to ensure Firestore has propagated the changes
      await new Promise(resolve => setTimeout(resolve, 300));

      // Refresh store data by reloading notes from subcollection
      const notesQuery = query(notesCollectionRef, orderBy('createdAt', 'desc'));
      const notesSnapshot = await getDocs(notesQuery);
      
      const notesData: Note[] = [];
      notesSnapshot.forEach((noteDoc) => {
        const noteData = noteDoc.data();
        
        // Ensure competitorData is properly structured
        let competitorData = undefined;
        if (noteData.competitorData && Array.isArray(noteData.competitorData)) {
          competitorData = noteData.competitorData.map((comp: any) => ({
            id: comp.id || '',
            name: comp.name || '',
            photoUrls: Array.isArray(comp.photoUrls) ? comp.photoUrls : (comp.photoUrls ? [comp.photoUrls] : []),
            stockLevel: comp.stockLevel || 0
          }));
        }
        
        notesData.push({
          id: noteDoc.id,
          text: noteData.text || '',
          date: noteData.date || '',
          time: noteData.time || '',
          author: noteData.author || '',
          salesRepName: noteData.salesRepName || '',
          visitType: noteData.visitType || undefined,
          competitors: noteData.competitors || [],
          competitorData: competitorData,
          media: noteData.media || [],
          documents: noteData.documents || [],
          nextVisitDate: noteData.nextVisitDate || '',
          storeLevel: noteData.storeLevel !== undefined && noteData.storeLevel !== null ? Number(noteData.storeLevel) : undefined,
          lowStockCategories: noteData.lowStockCategories || [],
          requestManagerContact: noteData.requestManagerContact || false,
          replies: noteData.replies || [],
          createdAt: noteData.createdAt
        });
      });
      
      const updatedStoreSnap = await getDoc(storeDocRef);
      if (updatedStoreSnap.exists()) {
        const updatedData = updatedStoreSnap.data();
        // Map store data fields to match desktop version structure
        setStore({
          id: updatedStoreSnap.id,
          CustomerNo: updatedData.CustomerNo || updatedData.customerNo || '',
          Address: updatedData.Address || updatedData.address || '',
          StoreName: updatedData.StoreName || updatedData.name || updatedData.StoreName || '',
          City: updatedData.City || updatedData.city || '',
          StoreNumber: updatedData.StoreNumber || updatedData.storeNumber || '',
          Prov: updatedData.Prov || updatedData.province || updatedData.state || '',
          PostalCode: updatedData.PostalCode || updatedData.postalCode || updatedData.pc || '',
          SortField: updatedData.SortField || updatedData.sortField || '',
          Telephone: updatedData.Telephone || updatedData.phone || '',
          SalesGroup: updatedData.SalesGroup || updatedData.salesGroup || '',
          EmailAdddress: updatedData.EmailAdddress || updatedData.EmailAddress || updatedData.email || '',
          SalespersonNo: updatedData.SalespersonNo || updatedData.salespersonNo,
          UDF_Outr_PickupNo: updatedData.UDF_Outr_PickupNo || updatedData.pickupNo || updatedData.PickupNumber,
          notes: notesData,
          // Include any other fields from updatedData
          ...updatedData
        } as Store);
      }

      console.log(t('storeDetails.noteSuccessConsole', '✅ Note submitted successfully with files uploaded to Firebase Storage'));
    } catch (err: any) {
      console.error(t('storeDetails.noteErrorConsole', '❌ Error submitting note:'), err);
      // Provide more detailed error message for debugging
      const errorMessage = err?.message || err?.toString() || 'Unknown error';
      console.error('Error details:', {
        message: errorMessage,
        stack: err?.stack,
        code: err?.code,
        name: err?.name
      });
      setError(`Failed to submit note: ${errorMessage}`);
      // Re-throw so the caller can handle it
      throw err;
    }
  };

  const handleSubmitReply = async (
    noteId: string,
    text: string,
    media: File[],
    documents: File[],
    compressionEnabled: boolean = true
  ) => {
    if (!storeId || !store) {
      console.error('Cannot submit reply: storeId or store is missing');
      throw new Error('Store information is missing');
    }
    
    try {
      const fileStorageService = FileStorageService.getInstance();
      const replyId = `reply-${Date.now()}`;
      const uploadedBy = user?.email || 'Unknown';

      // Upload files to Firebase Storage
      const uploadResults = await fileStorageService.uploadStoreNoteAttachments(
        storeId,
        replyId,
        media,
        documents,
        uploadedBy,
        compressionEnabled
      );

      // Convert upload results to Media and Document objects
      const mediaResults: Media[] = uploadResults.media.map(result => ({
        name: result.name,
        type: result.type,
        url: result.url
      }));

      const documentResults: Document[] = uploadResults.documents.map(result => ({
        name: result.name,
        type: result.type,
        url: result.url
      }));

      // Create the reply object
      const now = new Date();
      // Use explicit locale to avoid issues on Android browsers (Samsung Galaxy Chrome, etc.)
      const formattedDate = now.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit' 
      });
      const formattedTime = now.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      });
      const newReply: Note = {
        id: replyId,
        text,
        salesRepName: user?.email || 'Unknown',
        author: user?.email || 'Unknown',
        date: formattedDate,
        time: formattedTime,
        media: mediaResults,
        documents: documentResults,
        createdAt: Timestamp.now()
      };

      // Update the note document in the subcollection with the reply (matching desktop version)
      const notesCollectionRef = collection(firestore, 'Stores', storeId, 'notes');
      const noteDocRef = doc(notesCollectionRef, noteId);
      const noteDocSnap = await getDoc(noteDocRef);
      
      if (!noteDocSnap.exists()) {
        throw new Error('Note not found');
      }

      const noteData = noteDocSnap.data();
      const existingReplies = (noteData.replies || []) as Note[];
      
      // Add the new reply to the existing replies array
      const updatedReplies = [...existingReplies, newReply];

      // Update the note document in the subcollection
      await updateDoc(noteDocRef, {
        replies: updatedReplies
      });

      // Small delay to ensure Firestore has propagated the changes
      await new Promise(resolve => setTimeout(resolve, 300));

      // Reload notes from subcollection to get updated data
      const notesQuery = query(notesCollectionRef, orderBy('createdAt', 'desc'));
      const notesSnapshot = await getDocs(notesQuery);
      
      const notesData: Note[] = [];
      notesSnapshot.forEach((noteDoc) => {
        const noteData = noteDoc.data();
        notesData.push({
          id: noteDoc.id,
          text: noteData.text || '',
          date: noteData.date || '',
          time: noteData.time || '',
          author: noteData.author || '',
          salesRepName: noteData.salesRepName || '',
          competitors: noteData.competitors || [],
          media: noteData.media || [],
          documents: noteData.documents || [],
          nextVisitDate: noteData.nextVisitDate || '',
          storeLevel: noteData.storeLevel !== undefined && noteData.storeLevel !== null ? Number(noteData.storeLevel) : undefined,
          lowStockCategories: noteData.lowStockCategories || [],
          requestManagerContact: noteData.requestManagerContact || false,
          replies: noteData.replies || [],
          createdAt: noteData.createdAt
        });
      });

      // Reload store data to ensure consistency
      const storeDocRef = doc(firestore, 'Stores', storeId);
      const storeDocSnap = await getDoc(storeDocRef);
      
      if (storeDocSnap.exists()) {
        const storeData = storeDocSnap.data();
        // Map store data fields to match desktop version structure
        setStore({
          id: storeDocSnap.id,
          CustomerNo: storeData.CustomerNo || storeData.customerNo || '',
          Address: storeData.Address || storeData.address || '',
          StoreName: storeData.StoreName || storeData.name || storeData.StoreName || '',
          City: storeData.City || storeData.city || '',
          StoreNumber: storeData.StoreNumber || storeData.storeNumber || '',
          Prov: storeData.Prov || storeData.province || storeData.state || '',
          PostalCode: storeData.PostalCode || storeData.postalCode || storeData.pc || '',
          SortField: storeData.SortField || storeData.sortField || '',
          Telephone: storeData.Telephone || storeData.phone || '',
          SalesGroup: storeData.SalesGroup || storeData.salesGroup || '',
          EmailAdddress: storeData.EmailAdddress || storeData.EmailAddress || storeData.email || '',
          SalespersonNo: storeData.SalespersonNo || storeData.salespersonNo,
          UDF_Outr_PickupNo: storeData.UDF_Outr_PickupNo || storeData.pickupNo || storeData.PickupNumber,
          notes: notesData,
          // Include any other fields from storeData
          ...storeData
        } as Store);
      } else {
        // Fallback: just update notes if store doc not found
      setStore(prevStore => {
        if (!prevStore) return null;
        return {
          ...prevStore,
            notes: notesData
        };
      });
      }

      // Trigger notification for reply to note
      if (user?.id && noteData.author) {
        const { NotificationTriggerService } = await import('../../services/NotificationTriggerService');
        NotificationTriggerService.triggerNoteReplyNotification(
          storeId,
          noteId,
          user.id,
          noteData.author, // Original note author email
          store?.StoreName || store?.name
        ).catch(err => {
          console.error('Failed to trigger reply notification:', err);
          // Don't fail the reply submission if notification fails
        });
      }

      console.log('✅ Reply submitted successfully', { noteId, replyId, repliesCount: updatedReplies.length });
    } catch (err) {
      console.error('❌ Error submitting reply:', err);
      setError('Failed to submit reply');
      throw err;
    }
  };

  // Helper function to refetch notes
  const refetchNotes = async (): Promise<Note[]> => {
    if (!storeId) return [];
    
    try {
      const notesCollectionRef = collection(firestore, 'Stores', storeId, 'notes');
      const notesQuery = query(notesCollectionRef, orderBy('createdAt', 'desc'));
      const notesSnapshot = await getDocs(notesQuery);
      
      const notesData: Note[] = [];
      notesSnapshot.forEach((noteDoc) => {
        const noteData = noteDoc.data();
        notesData.push({
          id: noteDoc.id,
          text: noteData.text || '',
          date: noteData.date || '',
          time: noteData.time || '',
          author: noteData.author || '',
          salesRepName: noteData.salesRepName || '',
          competitors: noteData.competitors || [],
          media: noteData.media || [],
          documents: noteData.documents || [],
          nextVisitDate: noteData.nextVisitDate || '',
          storeLevel: noteData.storeLevel !== undefined && noteData.storeLevel !== null ? Number(noteData.storeLevel) : undefined,
          lowStockCategories: noteData.lowStockCategories || [],
          requestManagerContact: noteData.requestManagerContact || false,
          replies: noteData.replies || [],
          createdAt: noteData.createdAt
        });
      });
      
      return notesData;
    } catch (error) {
      console.error('Error refetching notes:', error);
      return [];
    }
  };

  const handleRefreshNotes = async () => {
    const notesData = await refetchNotes();
    if (notesData && notesData.length >= 0) {
      setStore(prevStore => {
        if (!prevStore) return null;
        return { ...prevStore, notes: notesData };
      });
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!storeId || !store) return;
    
    try {
      const notesCollectionRef = collection(firestore, 'Stores', storeId, 'notes');
      const noteDocRef = doc(notesCollectionRef, noteId);
      await deleteDoc(noteDocRef);
      
      const notesData = await refetchNotes();
      
      setStore(prevStore => {
        if (!prevStore) return null;
        return {
          ...prevStore,
          notes: notesData
        };
      });
      
      console.log('✅ Note deleted successfully');
    } catch (error) {
      console.error('❌ Error deleting note:', error);
      setError('Failed to delete note. Please try again.');
    }
  };

  const handleDeleteReply = async (noteId: string, replyId: string) => {
    if (!storeId || !store) return;
    
    try {
      const notesCollectionRef = collection(firestore, 'Stores', storeId, 'notes');
      const noteDocRef = doc(notesCollectionRef, noteId);
      const noteDocSnap = await getDoc(noteDocRef);
      
      if (!noteDocSnap.exists()) {
        throw new Error('Note not found');
      }
      
      const noteData = noteDocSnap.data();
      const existingReplies = (noteData.replies || []) as Note[];
      
      // Remove the reply from the array
      const updatedReplies = existingReplies.filter(reply => reply.id !== replyId);
      
      // Update the note document
      await updateDoc(noteDocRef, {
        replies: updatedReplies
      });
      
      // Reload notes
      const notesData = await refetchNotes();
      
      // Update local state
      setStore(prevStore => {
        if (!prevStore) return null;
        return {
          ...prevStore,
          notes: notesData
        };
      });
      
      console.log('✅ Reply deleted successfully');
    } catch (error) {
      console.error('❌ Error deleting reply:', error);
      setError('Failed to delete reply. Please try again.');
    }
  };

  const handleOpenLightbox = (imageUrl: string) => {
    setLightboxImage(imageUrl);
    setLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
    setLightboxImage(null);
  };

  const handleToggleMenu = () => {
    setIsMenuCollapsed(!isMenuCollapsed);
  };

  // Preview handlers
  const handleShowPreview = () => {
    setShowPreview(true);
    saveState('showPreview', 'true');
    window.scrollTo(0, 0);
    document.documentElement.scrollTo(0, 0);
  };

  const handleBackFromPreview = () => {
    setShowPreview(false);
    saveState('showPreview', 'false');
    window.scrollTo(0, 0);
    document.documentElement.scrollTo(0, 0);
  };

  const handleFinalSubmit = async (email: string) => {
    // This will be handled by the OrderPreviewScreen component
    // using the submitOrderWithEmail function from useGlobalOrderData
  };

  // Historical order handlers
  const handleOrderClick = (orderId: string) => {
    setSelectedHistoricalOrderId(orderId);
    saveState('selectedHistoricalOrderId', orderId);
  };

  const handleBackFromHistoricalPreview = () => {
    setSelectedHistoricalOrderId(null);
    saveState('selectedHistoricalOrderId', '');
    // Don't change tab here - let onNavigate handle it
  };


  const renderLayout = (children: React.ReactNode) => (
    <PageContainer isMenuCollapsed={true}>
      {children}
    </PageContainer>
  );

  if (isLoading) {
    return renderLayout(
      <MobileMainContent>
        <FlexColumn style={{ alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}>
          <MobileText size="lg">Loading store details...</MobileText>
        </FlexColumn>
      </MobileMainContent>
    );
  }

  if (error || !store) {
    return renderLayout(
      <MobileMainContent>
        <MobileCard>
          <FlexColumn style={{ alignItems: 'center' }} gap="16px">
            <MobileText color="error" size="lg">{error || 'Store not found'}</MobileText>
            <Button onClick={() => navigate('/store-visit')}>Back to Store Visit</Button>
          </FlexColumn>
        </MobileCard>
      </MobileMainContent>
    );
  }

  // Show historical order preview if an order is selected
  if (selectedHistoricalOrderId && storeId) {
    return renderLayout(
      <MobileMainContent>
        <HistoricalOrderPreviewScreen
          storeId={storeId}
          orderId={selectedHistoricalOrderId}
          onBack={handleBackFromHistoricalPreview}
          storeName={store.StoreName}
          onNavigate={(tab) => {
            // Handle navigation between tabs
            setSelectedHistoricalOrderId(null);
            saveState('selectedHistoricalOrderId', '');
            if (tab === 'store-history') {
              setActiveTab('store-history');
              saveState('activeTab', 'store-history');
            } else if (tab === 'store-visit') {
              setActiveTab('store-visit');
              saveState('activeTab', 'store-visit');
            } else if (tab === 'store-order') {
              setActiveTab('store-order');
              saveState('activeTab', 'store-order');
            }
          }}
        />
      </MobileMainContent>
    );
  }

  // Show preview screen if preview is active
  if (showPreview) {
    return (
      <GlobalOrderProvider storeId={storeId || ''}>
        {renderLayout(
          <MobileMainContent>
            {activeTab === 'order-supplies' ? (
              <OrderSuppliesPreviewScreen 
                onBack={handleBackFromPreview}
                onFinalSubmit={handleFinalSubmit}
                storeId={storeId}
                storeName={store.StoreName}
                onNavigate={(tab) => {
                  if (tab === 'store-visit') {
                    setShowPreview(false);
                  } else if (tab === 'store-history') {
                    setShowPreview(false);
                  }
                }}
              />
            ) : (
              <OrderPreviewScreen 
                onBack={handleBackFromPreview}
                onFinalSubmit={handleFinalSubmit}
                storeId={storeId}
                storeName={store.StoreName}
                orderType="store-order"
                onNavigate={(tab) => {
                  if (tab === 'store-visit') {
                    setShowPreview(false);
                  } else if (tab === 'store-history') {
                    setShowPreview(false);
                  }
                }}
              />
            )}
          </MobileMainContent>
        )}
      </GlobalOrderProvider>
    );
  }

  return (
    <GlobalOrderProvider storeId={storeId || ''}>
      {renderLayout(
        <MobileMainContent>
          <BackButton onClick={() => navigate('/store-visit')}>
            <ArrowLeft size={18} />
            {t('storeDetails.backToStores', 'Back to Stores List')}
          </BackButton>
          <BreadcrumbContainer>
            <BreadcrumbItem 
              clickable 
              onClick={() => navigate('/dashboard')}
            >
              <Home size={18} />
              <BreadcrumbText>{t('storeVisit.home', 'Home')}</BreadcrumbText>
            </BreadcrumbItem>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <BreadcrumbItem 
              clickable 
              onClick={() => navigate('/store-visit')}
            >
              <StoreIcon size={18} />
              <BreadcrumbText>{t('storeVisit.title', 'Store Visit')}</BreadcrumbText>
            </BreadcrumbItem>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <BreadcrumbItem>
              <BreadcrumbText>#{store.CustomerNo || 'N/A'}</BreadcrumbText>
            </BreadcrumbItem>
          </BreadcrumbContainer>
          <FlexColumn gap="24px">
            <TabbedInterface
              variant="mobile"
              middleSection={<CustomerAndOrderSections />}
              showMiddleSectionForTabs={['store-order']}
              defaultActiveTab="store-visit"
              activeTab={activeTab}
              onTabChange={(tab) => {
                tabChangeFromUiRef.current = true;
                setActiveTab(tab);
                saveState('activeTab', tab);
                setShowPreview(false);
                saveState('showPreview', 'false');
              }}
              tabs={[
                {
                  id: 'store-visit',
                  label: t('navigation.storeVisit', 'Store Visit'),
                  icon: StoreIcon,
                  content: (
                    <FlexColumn gap="24px">
                      <StoreInfoCard 
                        customerNumber={store.CustomerNo != null ? String(store.CustomerNo) : ''}
                        storeName={store.StoreName || ''}
                        address={store.Address || ''}
                        cityTown={store.City || ''}
                        storeNumber={store.StoreNumber || ''}
                        province={store.Prov || ''}
                        postalCode={store.PostalCode || ''}
                        sortField={store.SortField || ''}
                        phoneNumber={store.Telephone || ''}
                        salesGroup={store.SalesGroup || ''}
                        email={store.EmailAdddress || ''}
                      />
                      
                      <NotesSection 
                        onSubmitNote={async (note, salesRepName, competitors, attachments, nextVisitDate, compressionEnabled = true, storeLevel, lowStockCategories, requestManagerContact, competitorData, visitType, visitReason, onUploadProgress) => {
                          await handleSubmitNote(
                            note,
                            salesRepName,
                            competitors,
                            attachments,
                            nextVisitDate,
                            compressionEnabled,
                            storeLevel,
                            lowStockCategories,
                            requestManagerContact,
                            competitorData,
                            visitType,
                            visitReason,
                            onUploadProgress
                          );
                        }}
                        defaultSalesRepName={userFullName || user?.email || ''}
                        storeId={storeId}
                      />
                      
                      <NotesList 
                        notes={store.notes || []} 
                        onSubmitReply={handleSubmitReply}
                        onDeleteNote={handleDeleteNote}
                        onDeleteReply={handleDeleteReply}
                        onRefreshNotes={handleRefreshNotes}
                        currentUserEmail={user?.email}
                      />
                    </FlexColumn>
                  )
                },
                {
                  id: 'store-order',
                  label: t('storeDetails.storeOrder'),
                  icon: ShoppingCart,
                  subTabs: [
                    {
                      id: 'vegetable-order',
                      label: t('forms.vegetableOrder'),
                      icon: Carrot,
                      content: <OptimizedVegetableOrderForm storeId={storeId} onShowPreview={handleShowPreview} />
                    },
                    {
                      id: 'flower-order',
                      label: t('forms.flowerOrder'),
                      icon: Flower,
                      content: <OptimizedFlowerOrderForm storeId={storeId} onShowPreview={handleShowPreview} />
                    },
                    {
                      id: 'heritage-order',
                      label: t('forms.heritageOrder'),
                      icon: FileText,
                      content: <OptimizedHeritageOrderForm storeId={storeId} onShowPreview={handleShowPreview} />
                    },
                    {
                      id: 'livingston-order',
                      label: t('forms.livingstonOrder'),
                      icon: Package,
                      content: <OptimizedLivingstonOrderForm storeId={storeId} onShowPreview={handleShowPreview} />
                    },
                    {
                      id: 'planogram-order',
                      label: 'Planogram Order',
                      icon: LayoutGrid,
                      content: <PlanogramOrderForm storeId={storeId} onShowPreview={handleShowPreview} />
                    }
                  ]
                },
                {
                  id: 'store-history',
                  label: t('storeDetails.orderHistory'),
                  icon: History,
                  content: (
                    <StoreHistory 
                      storeId={storeId || ''} 
                      storeName={store.StoreName || ''}
                      onOrderClick={handleOrderClick}
                    />
                  )
                },
                {
                  id: 'order-supplies',
                  label: t('navigation.orderSupplies'),
                  icon: Package2,
                  content: (
                    <div>
                      <div style={{ marginBottom: Spacing.lg }}>
                        <Text style={{ color: ThemeColors.text.secondary, marginBottom: Spacing.md, fontSize: '14px' }}>
                          {t('storeDetails.orderPromotionalMaterials')}
                        </Text>
                      </div>
                      <StoreOrderSuppliesForm storeId={storeId} onShowPreview={handleShowPreview} />
                    </div>
                  )
                }
              ]}
            />
          </FlexColumn>
        </MobileMainContent>
      )}
    </GlobalOrderProvider>
  );
};